"use client";

import { useLocalePathname } from "@i18n/routing";
import { getModelDisplayName } from "@marketing/home/config/model-display-names";
import { getI2VModelBrand } from "@saas/image-to-video/config/model-brands";
import type { I2VModelId } from "@saas/image-to-video/types";
import { ModelBrandIcon } from "@saas/shared/components/ModelBrandIcon";
import {
	ClapperboardIcon,
	CombineIcon,
	FolderIcon,
	ImageIcon,
	ImagePlayIcon,
	ImagesIcon,
	LayersIcon,
	MicVocalIcon,
	MusicIcon,
	PenLineIcon,
	ReplaceIcon,
	SparklesIcon,
	TypeIcon,
	Wand2Icon,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { type ComponentType, type MouseEvent, createElement } from "react";

/**
 * 侧栏和移动端抽屉的唯一导航来源。两边以前各维护一份列表，结果桌面端加了
 * AI Video、移动端漏掉，手机上整整没有视频入口——所以合并到这里。
 *
 * `Icon` 给的是组件而不是渲染好的元素：侧栏用 h-5，抽屉用 h-4，尺寸交给各自决定。
 * 一般是 lucide 图标，Models 组是模型官方 logo（ModelBrandIcon）。
 */
export interface NavItem {
	href: string;
	title: string;
	Icon: ComponentType<{ className?: string }>;
	active: boolean;
	/** 标题右边的小角标（New），完整侧栏和手机抽屉显示，图标条不显示 */
	badge?: string;
	/** 悬停弹出的子菜单（侧栏）/ 缩进展开的子项（手机抽屉）。父项本身照样可点。
	 *  active 标出当前所在页，父项点进去的默认页也在里面，不标看着像重复（10/19） */
	children?: {
		href: string;
		title: string;
		Icon: LucideIcon;
		active: boolean;
		/** 标题右边的小角标，如新功能的 New */
		badge?: string;
	}[];
}

/**
 * 子项点击：目标就是当前页时（如在 /ai-edit-image 上点 Text to Image），Next 只换 query、
 * 不重新挂载页面，而工作台只在首次加载时读 ?tab=，tab 不会切。整页跳转让它重新读，
 * 这样 /ai-edit-image 的代码一行不用改。Cmd / Ctrl / Shift / 中键点击照常走浏览器（开新标签页）
 */
export function reloadIfSamePage(event: MouseEvent<HTMLAnchorElement>) {
	const isPlainClick =
		event.button === 0 &&
		!event.metaKey &&
		!event.ctrlKey &&
		!event.shiftKey &&
		!event.altKey;
	if (
		isPlainClick &&
		event.currentTarget.pathname === window.location.pathname
	) {
		event.preventDefault();
		window.location.assign(event.currentTarget.href);
	}
}

/** `/ai-tools` 落地页上的工具，顺序跟那个页面保持一致（shared/config/ai-tools.ts）。/ai-edit-image 不在这里：
 *  它是侧栏 AI Image 的落点，放这里会和 AI Image 同时高亮 */
const TOOL_PATHS = [
	"/templates/ai-clothes-changer",
	"/templates/ai-pose-generator",
	"/templates/getty-images-watermark-remover",
	"/templates/ai-background-remover",
	"/templates/virtual-staging-ai",
	"/image-to-prompt",
	"/batch-image-to-prompt",
	"/ai-describe-image",
	"/ai-outfit-describer",
	"/veo-prompt-generator",
];

/** `/ai-effects` 上的特效里挂在 Studio 侧栏下的那些（effects-catalog.ts）；
 *  视频热梗页已全部迁入 Studio */
const EFFECT_PATHS = [
	"/chuttamalle-ai-video-generator",
	"/zombie-trend-ai-video-generator",
	"/rumpelstiltskin-ai-video-generator",
	"/burning-bridges-ai-video-generator",
	"/bulin-47-ai-video-generator",

	"/hotel-lobby-ai-video-generator",
	"/ozil-goal-ai-video-generator",
	"/tesla-roadster-ai-video-generator",
	"/flying-car-ai",
	"/ai-kiss-video-generator",
	"/ai-french-kiss-video-generator",
	"/ai-wedding-video-generator",
	"/ai-hug-video-generator",
	"/ai-air-kiss-video-generator",
	"/ai-tongue-kiss-generator",
	"/ai-dance-video-generator",
	"/tiny-desk-ai-video-generator",
	"/transmission-5-dance-ai-video-generator",
	"/bye-bye-trend-ai-video-generator",
	"/hop-hop-hop-dance-ai-video-generator",
	"/photo-to-pixar",
	"/photo-to-anime",
	"/photo-to-cartoon",
	"/age-filter",
	"/templates/ai-smile-generator",
	"/ai-buzz-cut",
	"/templates/image-to-emoji-converter",
	"/ai-muscle-generator",
	"/ai-big-butt",
	"/ai-feet-generator",
];

/** 侧栏 Models 组：新的在前（照 SuperMaker），和页脚 Video Models 是同四页 */
const MODEL_PAGES: { href: string; model: I2VModelId }[] = [
	{ href: "/kling-4-ai-video-generator", model: "kling-4.0" },
	{ href: "/grok-ai-video-generator", model: "fast-3.0" },
	{ href: "/seedance-2-5-ai-video-generator", model: "seedance-2.5" },
	{ href: "/seedance-2-ai-video-generator", model: "seedance-2" },
];

/** 图标用模型下拉里那套官方 logo（Grok 用 xAI 的，Seedance 用字节的）；模块级建好，每次渲染是同一个组件 */
const MODEL_PAGE_ICONS = MODEL_PAGES.map(({ model }) => {
	const brand = getI2VModelBrand(model);
	return function ModelPageIcon({ className }: { className?: string }) {
		return createElement(ModelBrandIcon, { brand, className });
	};
});

export function useNavItems() {
	const t = useTranslations("saas.sidebar");
	// 子菜单的文生图 / 图生图直接用工作台 tab 的文案，两边叫法保持一致
	const tTabs = useTranslations("aiImageGenerator.client.tabs");
	const tVideoTabs = useTranslations("imageToVideo.client.tabs");
	const localePathname = useLocalePathname();
	const isActive = (...prefixes: string[]) =>
		prefixes.some((prefix) => localePathname.startsWith(prefix));

	const main: NavItem[] = [
		{
			href: "/my-assets",
			title: t("myAssets"),
			Icon: FolderIcon,
			active: isActive("/my-assets"),
		},
	];

	const creation: NavItem[] = [
		{
			// /ai-image-generator 已是营销落地页（#152），站内的图片工作台统一用 /ai-edit-image
			href: "/ai-edit-image",
			title: t("aiImage"),
			Icon: ImageIcon,
			active: isActive("/ai-edit-image", "/image-to-image"),
			// /ai-edit-image 本身一字不动（全站最大流量页）：Text to Image 只靠 ?tab= 切 tab；
			// Image to Image 是独立页，吃 image to image 词，和 edit 页的 editor 词分开
			// 按页面标当前项：Text to Image 只是 edit 页的一个 tab，工作台切 tab 不改网址，
			// 侧栏看不到，所以在 edit 页上一律标 Image Edit
			children: [
				{
					href: "/ai-edit-image?tab=text-to-image",
					title: tTabs("textToImage"),
					Icon: TypeIcon,
					active: false,
				},
				{
					href: "/image-to-image",
					title: tTabs("imageToImage"),
					Icon: ImagesIcon,
					active: isActive("/image-to-image"),
				},
				{
					href: "/ai-edit-image",
					title: t("imageEdit"),
					Icon: PenLineIcon,
					active: isActive("/ai-edit-image"),
				},
			],
		},
		{
			href: "/image-to-video",
			// 父项和 AI Image 对齐叫 AI Video，子项第一条仍叫 Image to Video（10/22 老公定）
			title: t("aiVideo"),
			Icon: ClapperboardIcon,
			active: isActive(
				"/image-to-video",
				"/start-end-frame-video",
				"/reference-to-video",
				"/genjutsu-ai",
			),
			// 每项是独立页、各有文案，都是同一个工作台换默认 tab（10/22 老公定）。
			// 首尾帧用工作台 tab 的叫法；第三项用页面名（tab 上叫 Reference，太长放不下）
			children: [
				{
					href: "/image-to-video",
					title: t("imageToVideo"),
					Icon: ImagePlayIcon,
					active: isActive("/image-to-video"),
				},
				{
					href: "/start-end-frame-video",
					title: tVideoTabs("between"),
					Icon: LayersIcon,
					active: isActive("/start-end-frame-video"),
				},
				{
					href: "/reference-to-video",
					title: t("referenceToVideo"),
					Icon: CombineIcon,
					active: isActive("/reference-to-video"),
					badge: t("new"),
				},
				{
					href: "/genjutsu-ai",
					title: t("genjutsuAi"),
					Icon: ReplaceIcon,
					active: isActive("/genjutsu-ai"),
					badge: t("new"),
				},
			],
		},
		{
			// 11/03 新模态：照 SuperMaker 排在 Video 后面，顶级项挂 New。
			// 11/04 加对口型 MV 页后改成组（老公定）：父项照 AI Video 组仍指向第一个子页
			href: "/ai-music-generator",
			title: t("aiMusic"),
			Icon: MusicIcon,
			active: isActive(
				"/ai-music-generator",
				"/ai-music-video-generator",
			),
			badge: t("new"),
			children: [
				{
					href: "/ai-music-generator",
					title: t("aiMusicGenerator"),
					Icon: MusicIcon,
					active: isActive("/ai-music-generator"),
				},
				{
					href: "/ai-music-video-generator",
					title: t("aiMusicVideo"),
					Icon: MicVocalIcon,
					active: isActive("/ai-music-video-generator"),
					badge: t("new"),
				},
			],
		},
		{
			href: "/ai-tools",
			title: t("aiTools"),
			Icon: Wand2Icon,
			active: isActive("/ai-tools", ...TOOL_PATHS),
		},
		{
			href: "/ai-effects",
			title: t("aiEffects"),
			Icon: SparklesIcon,
			active: isActive("/ai-effects", ...EFFECT_PATHS),
		},
	];

	// 模型页 10/28 起挪进 Studio，照 SuperMaker 在侧栏单列一组；名字用下拉里的模型名，不另写文案
	const models: NavItem[] = MODEL_PAGES.map(({ href, model }, index) => ({
		href,
		title: getModelDisplayName(model),
		Icon: MODEL_PAGE_ICONS[index],
		active: isActive(href),
	}));

	return { main, creation, models };
}
